import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createCard } from "../../components/ui/card";
import { createHeading } from "../../components/ui/heading";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import { createText } from "../../components/ui/text";
import {
  appendNextQueryToLoginUrl,
  readOrganizerReturnPathFromLocation,
} from "../../lib/organizer-return-url";
import { writeOrganizerSessionPendingHint } from "../../lib/organizer-session-client-hint";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";

export interface RenderOrganizerSignInOptions {
  /** Active UI locale (**`SupportedLocale`**), used by the header locale switcher. */
  locale: SupportedLocale;
  /** Cancels the optional header session probe when navigation tears the view down. */
  signal: AbortSignal;
}

/**
 * Sign-in shell at **`/organizer/sign-in`** (**DD-SRS-F-ORGANIZER-LOGIN.15**). Layout aligns with **`ux/organizer-login`** (header chrome, centred column, **`OAuth`** as primary controls).
 *
 * Provider **HTTP** paths are **`/api/v1/auth/login/{provider}`** on the site origin per **organizer-auth-common-prep**.
 *
 * @param mount - Container cleared and filled by this view.
 * @param t - Localized string resolver.
 * @param options - **`locale`** for the catalog picker (persists then reloads, same as home) and **`signal`** for header **`auth/me`**.
 */
export async function renderOrganizerSignIn(
  mount: HTMLElement,
  t: Translate,
  options: RenderOrganizerSignInOptions,
): Promise<void> {
  mount.replaceChildren();

  const shell = document.createElement("div");
  shell.className = "home-shell";

  shell.appendChild(
    createSkipLink(t("quiz.home.skip_to_main"), "organizer-sign-in-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  page.appendChild(header);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  const main = document.createElement("main");
  main.id = "organizer-sign-in-main";
  main.className = "organizer-login-main";

  const column = document.createElement("div");
  column.className = "organizer-login__column";

  const providersCard = createCard();
  providersCard.classList.add("organizer-login__providers-card");

  const headingId = "organizer-sign-in-heading";
  const h1 = createHeading(1, t("quiz.organizer.sign_in.title"));
  h1.id = headingId;
  providersCard.appendChild(h1);

  providersCard.appendChild(
    createText(t("quiz.organizer.sign_in.lead"), { muted: true }),
  );

  const mkOAuthLink = (
    href: string,
    labelKey: string,
    variant: "google" | "github",
  ): HTMLAnchorElement => {
    const a = document.createElement("a");
    a.href = href;
    a.textContent = t(labelKey);
    a.className =
      variant === "google"
        ? "organizer-login__oauth organizer-login__oauth--google qz-btn qz-btn--secondary"
        : "organizer-login__oauth organizer-login__oauth--github qz-btn";
    return a;
  };

  const nav = document.createElement("nav");
  nav.setAttribute("aria-labelledby", headingId);

  const returnPath = readOrganizerReturnPathFromLocation();

  const list = document.createElement("ul");
  list.className = "organizer-login__providers";

  const liGoogle = document.createElement("li");
  liGoogle.appendChild(
    mkOAuthLink(
      appendNextQueryToLoginUrl("/api/v1/auth/login/google", returnPath),
      "quiz.organizer.sign_in.google",
      "google",
    ),
  );

  const liGitHub = document.createElement("li");
  liGitHub.appendChild(
    mkOAuthLink(
      appendNextQueryToLoginUrl("/api/v1/auth/login/github", returnPath),
      "quiz.organizer.sign_in.github",
      "github",
    ),
  );

  list.appendChild(liGoogle);
  list.appendChild(liGitHub);
  nav.appendChild(list);
  providersCard.appendChild(nav);

  nav.addEventListener("click", (ev) => {
    const tEl = ev.target;
    if (!(tEl instanceof Element)) {
      return;
    }
    const link = tEl.closest("a");
    if (
      link instanceof HTMLAnchorElement &&
      link.classList.contains("organizer-login__oauth")
    ) {
      writeOrganizerSessionPendingHint();
    }
  });

  column.appendChild(providersCard);

  let bootstrapEnabled = false;
  try {
    const r = await fetch("/api/v1/auth/bootstrap/status", {
      credentials: "same-origin",
    });
    if (r.ok) {
      const body = (await r.json()) as { enabled?: boolean };
      bootstrapEnabled = body.enabled === true;
    }
  } catch {
    bootstrapEnabled = false;
  }

  if (bootstrapEnabled) {
    const bootCard = createCard();
    bootCard.classList.add("organizer-login__bootstrap-card");

    const h2 = createHeading(2, t("quiz.organizer.sign_in.bootstrap.heading"));
    bootCard.appendChild(h2);

    bootCard.appendChild(
      createText(t("quiz.organizer.sign_in.bootstrap.lead"), {
        muted: true,
      }),
    );

    const form = document.createElement("form");
    form.method = "post";
    form.action = "/api/v1/auth/login/bootstrap";
    form.className = "organizer-login__bootstrap-form";

    const userWrap = document.createElement("div");
    userWrap.className = "organizer-login__field";
    const userLabel = document.createElement("label");
    userLabel.htmlFor = "organizer-bootstrap-username";
    userLabel.textContent = t(
      "quiz.organizer.sign_in.bootstrap.username_label",
    );
    const userInput = document.createElement("input");
    userInput.id = "organizer-bootstrap-username";
    userInput.name = "username";
    userInput.type = "text";
    userInput.autocomplete = "username";
    userInput.required = true;
    userWrap.appendChild(userLabel);
    userWrap.appendChild(userInput);
    form.appendChild(userWrap);

    const passWrap = document.createElement("div");
    passWrap.className = "organizer-login__field";
    const passLabel = document.createElement("label");
    passLabel.htmlFor = "organizer-bootstrap-password";
    passLabel.textContent = t(
      "quiz.organizer.sign_in.bootstrap.password_label",
    );
    const passInput = document.createElement("input");
    passInput.id = "organizer-bootstrap-password";
    passInput.name = "password";
    passInput.type = "password";
    passInput.autocomplete = "current-password";
    passInput.required = true;
    passWrap.appendChild(passLabel);
    passWrap.appendChild(passInput);
    form.appendChild(passWrap);

    if (returnPath !== "/") {
      const nextInput = document.createElement("input");
      nextInput.type = "hidden";
      nextInput.name = "next";
      nextInput.value = returnPath;
      form.appendChild(nextInput);
    }

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className =
      "qz-btn qz-btn--primary organizer-login__bootstrap-submit";
    submit.textContent = t("quiz.organizer.sign_in.bootstrap.submit");
    form.appendChild(submit);

    form.addEventListener("submit", () => {
      writeOrganizerSessionPendingHint();
    });

    bootCard.appendChild(form);
    column.appendChild(bootCard);
  }

  main.appendChild(column);
  page.appendChild(main);

  mount.appendChild(shell);
}
